import { useMemo, useRef, useState } from 'react';
import { outputFileName } from '../core/exporter';
import { boundaryLength } from '../core/boundary';
import { maxTreeDepth, resolvedTreeLeafCount } from '../core/editor';
import { ModelViewport } from '../viewer/ModelViewport';
import { useAppStore } from './store';

export default function App() {
  const fileInput = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState<'editor' | 'help'>('editor');
  const [dragging, setDragging] = useState(false);
  const state = useAppStore();
  const document = state.document;
  const stats = useMemo(() => document ? {
    sourceFaces: document.parts.reduce((sum, part) => sum + part.triangles.length, 0),
    paintFaces: document.parts.reduce((sum, part) => sum + part.leaves.length, 0),
  } : null, [document, state.revision]);

  const openPicked = (files: FileList | null) => {
    const file = files?.[0]; if (file) void state.openFile(file);
  };
  const download = async () => {
    if (!document) return;
    const bytes = await state.exportFile(); if (!bytes) return;
    const blob = new Blob([bytes.slice().buffer], { type: 'model/3mf' });
    const url = URL.createObjectURL(blob); const anchor = window.document.createElement('a');
    anchor.href = url; anchor.download = outputFileName(document.fileName); anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return <div
    className="app-shell"
    onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
    onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
    onDrop={(event) => { event.preventDefault(); setDragging(false); openPicked(event.dataTransfer.files); }}
  >
    <input ref={fileInput} type="file" accept=".3mf,model/3mf" hidden onChange={(event) => openPicked(event.target.files)} />
    <nav className="top-tabs" aria-label="Application pages">
      <button className={page === 'editor' ? 'active' : ''} onClick={() => setPage('editor')}>Editor</button>
      <button className={page === 'help' ? 'active' : ''} onClick={() => setPage('help')}>Help</button>
      <span className="document-name" title={document?.fileName}>{document?.fileName || 'No project loaded'}</span>
      <span className="privacy-mark">LOCAL PROCESSING</span>
    </nav>
    <div className="toolbar">
      <button className="primary" onClick={() => fileInput.current?.click()}>Open 3MF</button>
      <span className="toolbar-separator" />
      <button disabled={!state.undoStack.length} onClick={state.undo}>Undo</button>
      <button disabled={!state.redoStack.length} onClick={state.redo}>Redo</button>
      <span className="toolbar-spacer" />
      <button disabled={!document} className="primary" onClick={() => void download()}>Export 3MF</button>
    </div>

    {page === 'help' ? <HelpPage /> : <main className="workspace">
      <ControlRail />
      <section className="viewer-panel">
        {document ? <ModelViewport document={document} revision={state.revision} /> : <EmptyWorkspace onOpen={() => fileInput.current?.click()} />}
        {dragging && <div className="drop-overlay">Drop 3MF to open</div>}
        {state.error && <div className="message error"><span>{state.error}</span><button onClick={state.clearError}>×</button></div>}
        {!!document?.warnings.length && <div className="message warning">{document.warnings.join(' ')}</div>}
      </section>
    </main>}

    <footer className="statusbar">
      <span className="status-led" data-error={!!state.error} />
      <span>{state.status}</span>
      <span className="status-spacer" />
      {document && stats && <>
        <span>{document.filaments.length} filaments</span><span>{stats.sourceFaces.toLocaleString()} source faces</span>
        <span>{stats.paintFaces.toLocaleString()} paint faces</span><span>{document.boundaryGroups.length} boundaries</span>
        <span>{document.unit}</span>
      </>}
    </footer>
  </div>;
}

function ControlRail() {
  const state = useAppStore(); const document = state.document;
  const selected = document?.boundaryGroups.find((group) => group.id === state.selectedBoundaryId);
  const selectedChains = selected ? document?.boundaries.filter((chain) => selected.chainIds.includes(chain.id)) ?? [] : [];
  const debug = new URLSearchParams(location.search).has('debug');
  return <aside className="control-rail">
    <section className="rail-section">
      <h2>Filaments</h2>
      <div className="palette" aria-label="Filament palette">
        {document?.filaments.map((filament) => <button
          key={filament.index}
          className={`filament-row ${state.selectedExtruder === filament.index ? 'selected' : ''}`}
          onClick={() => state.setExtruder(filament.index)}
        >
          <span className="swatch" style={{ backgroundColor: filament.color }} />
          <span>{filament.name}</span><code>E{filament.index}</code>
        </button>)}
        {!document && <span className="muted">Open a project to read its palette.</span>}
      </div>
    </section>

    <section className="rail-section">
      <h2>Bucket Fill</h2>
      <p className="mode-line"><span className="tool-symbol">▰</span> Active tool <kbd>B</kbd></p>
      <RangeControl label="Slope limit" value={state.slopeLimit} min={0} max={90} step={1} unit="°" onChange={state.setSlope} />
      <div className="inline-note">Local face-to-face angle</div>
    </section>

    <section className="rail-section boundary-controls">
      <h2>Boundary</h2>
      <label className="field-row"><span>Overlay</span><select value={state.boundaryMode} onChange={(event) => state.setBoundaryMode(event.target.value as 'off' | 'smoothed' | 'compare')}>
        <option value="off">Off</option><option value="smoothed">Smoothed</option><option value="compare">Original + smoothed</option>
      </select></label>
      <RangeControl label="Smoothness" value={state.smoothOptions.strength} min={0} max={100} step={1} unit="%" onChange={(value) => state.setSmoothOption('strength', value)} />
      <label className="field-row"><span>Selected</span><select value={state.selectedBoundaryId || ''} onChange={(event) => state.selectBoundary(event.target.value || null)} disabled={!document?.boundaryGroups.length}>
        <option value="">None</option>
        {document?.boundaryGroups.map((group, index) => <option key={group.id} value={group.id}>B{index + 1} · E{group.colorA}/E{group.colorB}</option>)}
      </select></label>
      {selected && <div className="selection-readout">{selectedChains.reduce((sum, chain) => sum + boundaryLength(chain), 0).toFixed(1)} mm · {selectedChains.every((chain) => chain.closed) ? 'closed' : 'continuous'} · {selectedChains.reduce((sum, chain) => sum + chain.originalPoints.length, 0)} nodes</div>}
      <div className="button-grid">
        <button disabled={!selected} onClick={state.applySelectedSmooth}>Smooth Selected</button>
        <button disabled={!document?.boundaryGroups.length} onClick={state.applyAllSmooth}>Smooth All</button>
      </div>
      <details>
        <summary>Advanced</summary>
        <RangeControl label="Max movement" value={state.smoothOptions.maxMovement} min={0.05} max={5} step={0.05} unit="mm" decimals={2} onChange={(value) => state.setSmoothOption('maxMovement', value)} />
        <RangeControl label="Export tolerance" value={state.smoothOptions.tolerance} min={0.01} max={0.25} step={0.01} unit="mm" decimals={2} onChange={(value) => state.setSmoothOption('tolerance', value)} />
        <RangeControl label="Crease lock" value={state.smoothOptions.creaseAngle} min={20} max={80} step={1} unit="°" onChange={(value) => state.setSmoothOption('creaseAngle', value)} />
      </details>
    </section>
    {debug && <DebugPanel />}
  </aside>;
}

function RangeControl(props: { label: string; value: number; min: number; max: number; step: number; unit: string; decimals?: number; onChange(value: number): void }) {
  return <div className="range-control">
    <label><span>{props.label}</span><output>{props.value.toFixed(props.decimals ?? 0)} {props.unit}</output></label>
    <input type="range" value={props.value} min={props.min} max={props.max} step={props.step} onChange={(event) => props.onChange(Number(event.target.value))} />
  </div>;
}

function DebugPanel() {
  const state = useAppStore(); const document = state.document;
  if (!document) return null;
  const trees = document.parts.flatMap((part) => part.triangles.map((triangle) => triangle.tree));
  return <section className="rail-section debug-panel">
    <h2>Diagnostics</h2>
    <dl>
      <dt>Parts</dt><dd>{document.parts.length}</dd>
      <dt>Tree leaves</dt><dd>{trees.reduce((sum, tree) => sum + resolvedTreeLeafCount(tree), 0).toLocaleString()}</dd>
      <dt>Max depth</dt><dd>{Math.max(...trees.map(maxTreeDepth))}</dd>
      <dt>Boundary fragments</dt><dd>{document.boundaries.length}</dd>
      <dt>Selectable boundaries</dt><dd>{document.boundaryGroups.length}</dd>
      <dt>Dirty</dt><dd>{document.dirty ? 'yes' : 'no'}</dd>
    </dl>
  </section>;
}

function EmptyWorkspace({ onOpen }: { onOpen(): void }) {
  return <div className="empty-workspace">
    <svg viewBox="0 0 96 96" aria-hidden="true"><path d="M48 11 78 27 48 43 18 27Z"/><path d="m18 27 30 16v38L18 65Z"/><path d="m78 27-30 16v38l30-16Z"/><path className="paint-mark" d="m48 43 30-16v15L48 58Z"/></svg>
    <strong>No 3MF loaded</strong>
    <span>Drop an OrcaSlicer or Bambu Studio project here</span>
    <button className="primary" onClick={onOpen}>Open 3MF</button>
    <small>Files stay in this browser and are never uploaded.</small>
  </div>;
}

function HelpPage() {
  return <main className="help-page">
    <article>
      <h1>3MF Paint Editor</h1>
      <p>Edits OrcaSlicer and Bambu Studio filament painting without changing the source mesh, placement, slicer settings, support paint, seam paint, fuzzy-skin paint, or unrelated archive entries.</p>
      <h2>Workflow</h2>
      <ol><li>Open a project <code>.3mf</code>.</li><li>Select an existing filament slot.</li><li>Click a surface to bucket-fill a connected same-colour region.</li><li>Select a boundary line, adjust Smoothness, and apply.</li><li>Export and open the resulting file in OrcaSlicer.</li></ol>
      <h2>Controls</h2>
      <dl><dt>Slope limit</dt><dd>Maximum local angle crossed by bucket fill. It compares each face to its neighbour, not every face to the seed.</dd><dt>Maximum movement</dt><dd>Hard limit on how far the target boundary may move from the painted boundary.</dd><dt>Export tolerance</dt><dd>Maximum target approximation scale used by the recursive Orca paint tree. The default is 0.05 mm.</dd><dt>Crease lock</dt><dd>Boundaries that predominantly follow a sharper model crease are left in place.</dd></dl>
      <h2>Format behaviour</h2>
      <p>The smooth line is an internal surface-constrained target. Export approximates it with Orca-compatible recursive triangle subdivision; a 3MF does not store an analytic spline. Unchanged triangle attributes and ZIP entries are preserved. Exporting before any edit returns the original bytes.</p>
      <h2>Limits</h2>
      <p>The editor accepts up to 500 MB compressed input, 2 million source triangles, and 3 million decoded paint leaves. Non-manifold or branched boundaries are split into fixed-ended chains. Always verify a new output in your slicer before printing.</p>
      <h2>Privacy</h2>
      <p>Parsing, painting, smoothing, and export occur locally. There is no model upload endpoint, telemetry, or filename collection.</p>
    </article>
  </main>;
}
